import { useCallback, useEffect, useMemo, useState } from "react";

import {
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { format, isValid } from "date-fns";
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { BaseLabel } from "components";

import { usePermissions } from "hooks/permission";
import { useToast } from "hooks/toast";

import { status } from "features/hearingRecords/LeftList/util";

import { InputGeneral } from "components/Input";

import { audiencesServices } from "services/audiences";

import { FORMS_HEARING } from "utils/entities/permission";

import {
  Container,
  ContainerAutoComplete,
  ContainerBody,
  ContainerInputs,
  StyledAutocomplete,
  SelectedHearingCard,
  SelectedHearingItem,
  MigrationContainer,
  MigrationTitle,
  // ContainerLoading,
  // TextLoading,
} from "./style";

const MAX_CHARS = 50;
type SwapMode = 0 | 1 | 2;

interface ValidationProps {
  value: string;
  placeId: number;
  setHearingToMigrate: (hearing: any) => void;
  setSwapModeToMigrate?: (swapMode: SwapMode) => void;
  currentHearingId?: number;
  userId?: number;
}

const VinculeDataToAnotherHearingModal = ({
  value,
  placeId,
  setHearingToMigrate,
  setSwapModeToMigrate,
  currentHearingId,
  userId,
}: ValidationProps) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [loadingProcedure, setLoadingProcedure] = useState(false);
  const [hearingList, setHearingList] = useState<Array<any>>([]);
  const [nextProcedureNumber, setNextProcedureNumber] = useState("");
  const [hearingSelected, setHearingSelected] = useState<any>(null);
  const [swapMode, setSwapMode] = useState<SwapMode>(0);

  const { configurations } = useSelector((store: any) => store);
  const { viewOnlyYourRecords } = usePermissions(FORMS_HEARING.HEARING);

  const procedureMask = useMemo(() => {
    return configurations?.globalConfigurations?.find(
      (config: any) => config.name === "ProcedureMask",
    )?.value;
  }, [configurations]);

  const fetchHearing = async (data: string) => {
    try {
      const response = await audiencesServices.getAllByFiltersV2({
        procedureNumber: data || undefined,
        placeId: placeId,
        userId: viewOnlyYourRecords ? userId : undefined,
      });
      const parsedResponse = response.data?.map((item: any) => ({
        ...item,
        label: `${item.procedureNumber} - ${item.hearingTypeName}`,
        value: item.id,
        expectedDate: item.expectedDate ? new Date(item.expectedDate) : null,
      }));
      setHearingList(parsedResponse);
      setHearingSelected(null);
      setHearingToMigrate(null);
    } catch (error) {
      addToast({
        type: "error",
        title: "Erro ao buscar processos",
        description:
          "Ocorreu um erro ao buscar os processos. Tente novamente mais tarde.",
      });
    } finally {
      setLoadingProcedure(false);
    }
  };

  const debouncedFetch = useCallback(
    debounce((value: string) => {
      fetchHearing(value);
    }, 1000),
    [],
  );

  const formattedExpectedDate =
    hearingSelected?.expectedDate && isValid(hearingSelected.expectedDate)
      ? format(hearingSelected.expectedDate, "dd/MM/yyyy HH:mm")
      : "-";

  useEffect(() => {
    setSwapModeToMigrate?.(swapMode);
  }, [swapMode, setSwapModeToMigrate]);

  return (
    <Container>
      <ContainerBody>
        <ContainerInputs>
          <InputGeneral
            value={value}
            maxLength={100}
            onChange={() => {}}
            type="text"
            label={t("migration.currentProcedureNumber")}
            disabled
          />
        </ContainerInputs>
        <ContainerAutoComplete>
          <BaseLabel marginLeft="0px" color="#666666">
            {t("migration.targetProcedureNumber")}
          </BaseLabel>
          <StyledAutocomplete
            value={nextProcedureNumber}
            loading={loadingProcedure}
            style={{ marginTop: 0 }}
            options={hearingList || []}
            noOptionsText={t("common.noOptionsFound")}
            onInputChange={(_e, newInputValue, reason) => {
              if (reason === "clear") {
                setHearingList([]);
                setHearingSelected(null);
                setHearingToMigrate(null);
                setNextProcedureNumber("");
                return;
              }

              if (reason !== "input") {
                return;
              }
              setHearingSelected(null);
              setHearingToMigrate(null);
              const limited = newInputValue.slice(0, MAX_CHARS);
              const display =
                newInputValue.length > MAX_CHARS ? `${limited}...` : limited;
              setNextProcedureNumber(display);

              if (limited.trim().length === procedureMask?.length) {
                setLoadingProcedure(true);
                debouncedFetch(limited);
              }
              if (limited.trim().length === 0) {
                setHearingList([]);
              }
            }}
            onChange={(_, option: any) => {
              if (!option) {
                setHearingSelected(null);
                setHearingToMigrate(null);
                return;
              }
              const label = option.label ?? String(option.value);
              const limited = label.slice(0, MAX_CHARS);
              const display =
                label.length > MAX_CHARS ? `${limited}...` : limited;
              setNextProcedureNumber(display);
              const resp = hearingList.find((item) => item.id === option.value);

              if (!resp) {
                setHearingSelected(null);
                setHearingToMigrate(null);
                return;
              }

              if (currentHearingId && resp.id === currentHearingId) {
                addToast({
                  type: "warning",
                  title: t("migration.invalidProcedure"),
                  description: t("migration.cannotMigrateToSame"),
                });
                setHearingSelected(null);
                setHearingToMigrate(null);
                setNextProcedureNumber("");
                return;
              }

              setHearingSelected(resp);
              setHearingToMigrate(resp);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={t("migration.typeProcedureNumber")}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingProcedure ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </ContainerAutoComplete>
        {hearingSelected && (
          <SelectedHearingCard>
            <SelectedHearingItem>
              <strong>Número do processo:</strong>
              <span>{hearingSelected?.procedureNumber || "-"}</span>
            </SelectedHearingItem>
            <SelectedHearingItem>
              <strong>{t("audiences.type")}:</strong>
              <span>{hearingSelected?.hearingTypeName || "-"}</span>
            </SelectedHearingItem>
            <SelectedHearingItem>
              <strong>Status:</strong>
              <span>
                {hearingSelected?.status
                  ? status(hearingSelected.status).status
                  : "-"}
              </span>
            </SelectedHearingItem>
            <SelectedHearingItem>
              <strong>{t("audiences.dateLabel")}:</strong>
              <span>{formattedExpectedDate}</span>
            </SelectedHearingItem>
          </SelectedHearingCard>
        )}
        <MigrationContainer>
          <MigrationTitle>{t("audiences.migration.modeTitle")}</MigrationTitle>
          <RadioGroup
            value={swapMode}
            onChange={(event) =>
              setSwapMode(Number(event.target.value) as SwapMode)
            }
          >
            <FormControlLabel
              value={0}
              control={<Radio size="small" />}
              label="Completa"
            />
            <FormControlLabel
              value={1}
              control={<Radio size="small" />}
              label="Somente vídeo"
            />
            <FormControlLabel
              value={2}
              control={<Radio size="small" />}
              label="Somente participantes"
            />
          </RadioGroup>
        </MigrationContainer>
      </ContainerBody>
    </Container>
  );
};

export default VinculeDataToAnotherHearingModal;
